import { CARCASS_MM } from "./carcass";
import type { ShelvingCellConfig, ShelvingFacadeMount, WardrobeModule } from "../types";

export const SHELVING_MIN_WIDTH_MM = 300;
export const SHELVING_MAX_WIDTH_MM = 4500;
export const SHELVING_MIN_HEIGHT_MM = 300;
export const SHELVING_MAX_HEIGHT_MM = 2750;
export const SHELVING_MIN_DEPTH_MM = 200;
export const SHELVING_MAX_DEPTH_MM = 450;
export const SHELVING_MIN_COLUMNS = 2;
export const SHELVING_MAX_COLUMNS = 8;
export const SHELVING_MIN_ROWS = 1;
export const SHELVING_MAX_ROWS = 12;
export const SHELVING_PANEL_THICKNESS_MM = CARCASS_MM;
/** Стеклянная полка ячейки, мм (предварительно, уточнить у Сергея). */
export const SHELVING_GLASS_SHELF_THICKNESS_MM = 6;
export const SHELVING_PLINTH_HEIGHT_MM = 72;
export const SHELVING_LEG_HEIGHT_MM = 100;
export const SHELVING_MIN_CELL_WIDTH_MM = 100;
export const SHELVING_MIN_CELL_HEIGHT_MM = 100;
export const SHELVING_EXTRA_SHELF_CLEARANCE_MM = 100;
export const SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM = 100;
export const SHELVING_EXTRA_DIVIDER_MAX_ANGLE_DEGREES = 25;
export const SHELVING_EXTRA_DIVIDER_MIN_ANGLE_DEGREES = 3;
export const SHELVING_BACK_PANEL_THICKNESS_MM = 8;
export const SHELVING_CELL_BACK_PANEL_OVERLAP_MM = 4;
export const SHELVING_LEG_EDGE_INSET_MM = 54;
export const SHELVING_MAX_LEG_SPAN_MM = 600;
/**
 * Наибольший пролёт полки без промежуточной опоры. Шире — полка со временем
 * прогибается, поэтому проём обязан быть разделён стойкой.
 */
export const SHELVING_MAX_CLEAR_SPAN_MM = 1200;

export const SHELVING_DOOR_MIN_WIDTH_MM = 180;
export const SHELVING_DOOR_MIN_HEIGHT_MM = 180;
export const SHELVING_DRAWERS_MIN_WIDTH_MM = 220;
export const SHELVING_DRAWERS_MIN_HEIGHT_MM = 250;

/**
 * Single source of truth for "does this filling physically fit the opening".
 * The cell editor disables the control, validation warns about saved projects
 * and bulk apply skips the cell — all three ask this.
 */
/**
 * Ящик — не резиновый. Фасад ниже минимума не за что взять, выше максимума —
 * это уже не ящик, а бункер: в мебели такой проём набирают несколькими ящиками.
 */
export const SHELVING_DRAWER_MIN_FRONT_MM = 250;
export const SHELVING_DRAWER_MAX_FRONT_MM = 500;
export const SHELVING_DRAWER_MAX_COUNT = 7;
/**
 * Комод (видео Сергея 24.10): в колонке от одного до пяти ящиков, фасад — от
 * 150 мм, чтобы в комоде 900 мм встало пять ящиков.
 */
export const CHEST_DRAWER_MIN_FRONT_MM = 150;
export const CHEST_MAX_DRAWERS = 5;

export interface DrawerLimits { minFrontMm: number; maxCount: number }
const SHELVING_DRAWER_LIMITS: DrawerLimits = { minFrontMm: SHELVING_DRAWER_MIN_FRONT_MM, maxCount: SHELVING_DRAWER_MAX_COUNT };
const CHEST_DRAWER_LIMITS: DrawerLimits = { minFrontMm: CHEST_DRAWER_MIN_FRONT_MM, maxCount: CHEST_MAX_DRAWERS };
/** Пределы ящиков изделия: у комода свои. */
export const drawerLimits = (module?: { shelvingKind?: string } | null): DrawerLimits =>
  module?.shelvingKind === "chest" ? CHEST_DRAWER_LIMITS : SHELVING_DRAWER_LIMITS;

/** Сколько ящиков допустимо в проёме такой высоты. */
export const shelvingDrawerCountRange = (cellHeight: number, limits: DrawerLimits = SHELVING_DRAWER_LIMITS) => {
  const height = Math.max(1, cellHeight);
  const min = Math.min(
    limits.maxCount,
    Math.max(1, Math.ceil(height / SHELVING_DRAWER_MAX_FRONT_MM))
  );
  const max = Math.max(
    min,
    Math.min(limits.maxCount, Math.floor(height / limits.minFrontMm))
  );
  return { min, max };
};

/**
 * Пожелание пользователя, зажатое высотой проёма. Само число в ячейке не
 * переписывается: если проём снова станет ниже, вернётся выбранное значение.
 */
export const shelvingDrawerCount = (
  drawerCount: number | undefined,
  cellHeight: number,
  limits: DrawerLimits = SHELVING_DRAWER_LIMITS
) => {
  const range = shelvingDrawerCountRange(cellHeight, limits);
  return clamp(Math.round(drawerCount ?? 2), range.min, range.max);
};

/** Высота одного фасада в проёме — то, что человек увидит в изделии. */
export const shelvingDrawerFrontHeight = (
  drawerCount: number | undefined,
  cellHeight: number
) => Math.max(1, cellHeight) / shelvingDrawerCount(drawerCount, cellHeight);

export const shelvingCellFits = (
  content: ShelvingCellConfig["content"],
  width: number,
  height: number
) => {
  if (content === "door") {
    return width >= SHELVING_DOOR_MIN_WIDTH_MM && height >= SHELVING_DOOR_MIN_HEIGHT_MM;
  }
  if (content === "drawers") {
    return (
      width >= SHELVING_DRAWERS_MIN_WIDTH_MM &&
      height >= SHELVING_DRAWERS_MIN_HEIGHT_MM
    );
  }
  return true;
};

export const SHELVING_DIAGONAL_ANGLE_DEGREES = 58;
export const SHELVING_DIAGONAL_MIN_SPACING_MM = 220;
export const SHELVING_DIAGONAL_MAX_SPACING_MM = 360;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/** Отрезок прямой под углом angle со сдвигом normalOffset, обрезанный прямоугольником. */
export const clippedLineInRectangle = (
  angle: number,
  normalOffset: number,
  halfWidth: number,
  halfHeight: number
) => {
  const directionX = Math.cos(angle);
  const directionY = Math.sin(angle);
  const originX = -directionY * normalOffset;
  const originY = directionX * normalOffset;
  let minimum = Number.NEGATIVE_INFINITY;
  let maximum = Number.POSITIVE_INFINITY;
  const clipAxis = (origin: number, direction: number, halfSize: number) => {
    if (Math.abs(direction) < 1e-8) return Math.abs(origin) <= halfSize;
    const first = (-halfSize - origin) / direction;
    const second = (halfSize - origin) / direction;
    minimum = Math.max(minimum, Math.min(first, second));
    maximum = Math.min(maximum, Math.max(first, second));
    return minimum <= maximum;
  };
  if (!clipAxis(originX, directionX, halfWidth) || !clipAxis(originY, directionY, halfHeight)) return null;
  return {
    startX: originX + directionX * minimum,
    startY: originY + directionY * minimum,
    endX: originX + directionX * maximum,
    endY: originY + directionY * maximum
  };
};

export interface ShelvingLatticeBoard {
  /** Центр доски от центра секции, мм. */
  x: number;
  y: number;
  /** Длина доски с заходом 8 мм в боковые стенки секции, мм. */
  length: number;
  depth: number;
  /** Угол доски к горизонтали, рад. */
  angle: number;
}

/**
 * Доски диагональной решётки секции — одни и те же для 3D, сметы и техкарты.
 * Две серии досок под ±58° с шагом 220–360 мм; обрезки короче 120 мм не ставятся.
 */
export const shelvingLatticeBoards = (
  section: { width: number; height: number },
  depth: number
): ShelvingLatticeBoard[] => {
  const angleMagnitude = (SHELVING_DIAGONAL_ANGLE_DEGREES * Math.PI) / 180;
  const spacing = clamp(
    Math.min(section.width, section.height) * 0.34,
    SHELVING_DIAGONAL_MIN_SPACING_MM,
    SHELVING_DIAGONAL_MAX_SPACING_MM
  );
  const boardDepth = Math.max(80, depth - 10);
  const minimumLength = Math.max(SHELVING_PANEL_THICKNESS_MM * 4, 120);
  const boards: ShelvingLatticeBoard[] = [];
  [angleMagnitude, -angleMagnitude].forEach((angle) => {
    const extent =
      Math.abs(Math.sin(angle)) * section.width / 2 +
      Math.abs(Math.cos(angle)) * section.height / 2;
    const lineCount = Math.ceil(extent / spacing);
    for (let index = -lineCount; index <= lineCount; index += 1) {
      const segment = clippedLineInRectangle(angle, index * spacing, section.width / 2, section.height / 2);
      if (!segment) continue;
      const deltaX = segment.endX - segment.startX;
      const deltaY = segment.endY - segment.startY;
      const length = Math.hypot(deltaX, deltaY);
      if (length < minimumLength) continue;
      boards.push({
        x: (segment.startX + segment.endX) / 2,
        y: (segment.startY + segment.endY) / 2,
        length: length + 8,
        depth: boardDepth,
        angle: Math.atan2(deltaY, deltaX)
      });
    }
  });
  return boards;
};

/**
 * An individual rear panel is mounted behind the opening and continues under
 * the surrounding shelves and uprights. The overlap prevents a visible light
 * gap and gives the installer a reliable fixing edge on every side.
 */
export const shelvingCellBackPanelSizeMm = (width: number, height: number) => ({
  width: Math.max(40, width + SHELVING_CELL_BACK_PANEL_OVERLAP_MM * 2),
  height: Math.max(40, height + SHELVING_CELL_BACK_PANEL_OVERLAP_MM * 2)
});

/** Outer sides rise with an open-leg base, but reach the floor for a plinth
 * or a floor-standing carcass. Shared by 3D, pricing and production output.
 */
export const shelvingSideBottomMm = (module: WardrobeModule) =>
  module.shelvingBase === "legs" ? SHELVING_LEG_HEIGHT_MM : 0;

/**
 * Крышка стеллажа и комода — накладная (Сергей, 27.10): лежит на боковинах во
 * всю ширину, боковины ниже на её толщину, фасады верхнего ряда — под ней.
 * Своя столешница комода (15.10) — так же, торцы и кромки в её декоре.
 */
export const shelvingTopOverSides = (module: Pick<WardrobeModule, "type">) =>
  module.type === "shelving";

/** Зазор между фасадами верхнего ряда и столешницей, лежащей на боковинах, мм. */
export const SHELVING_TOP_FACADE_GAP_MM = 3;

export const shelvingSideHeightMm = (module: WardrobeModule) =>
  Math.max(SHELVING_PANEL_THICKNESS_MM, module.height - shelvingSideBottomMm(module) -
    (shelvingTopOverSides(module) ? SHELVING_PANEL_THICKNESS_MM : 0));

/** Keep wide shelves supported at no more than a 600 mm span. */
export const shelvingLegXPositionsMm = (width: number) => {
  const inset = Math.min(SHELVING_LEG_EDGE_INSET_MM, Math.max(0, width / 2 - 18));
  const left = -width / 2 + inset;
  const right = width / 2 - inset;
  const span = Math.max(0, right - left);
  const intervals = Math.max(1, Math.ceil(span / SHELVING_MAX_LEG_SPAN_MM));
  return Array.from({ length: intervals + 1 }, (_, index) =>
    left + span * index / intervals
  );
};

export const shelvingLegCount = (module: WardrobeModule) =>
  module.shelvingBase === "legs"
    ? shelvingLegXPositionsMm(module.width).length * 2
    : 0;

export const normalizeShelvingRatios = (
  values: number[] | undefined,
  count: number
) => {
  const safeCount = Math.max(1, Math.round(count));
  if (!values || values.length !== safeCount) {
    return Array.from({ length: safeCount }, () => 1 / safeCount);
  }
  const cleaned = values.map((value) =>
    Number.isFinite(value) && value > 0 ? value : 0
  );
  const sum = cleaned.reduce((total, value) => total + value, 0);
  if (sum <= 0) {
    return Array.from({ length: safeCount }, () => 1 / safeCount);
  }
  return cleaned.map((value) => value / sum);
};

/**
 * Наименьшее число проёмов, при котором ни один не шире допустимого пролёта.
 * Задаёт нижнюю границу выбора: узкий стеллаж обходится двумя стойками,
 * широкому их добавляют автоматически.
 */
/** Комод может быть в одну колонку ящиков, стеллаж — от двух колонок. */
export const shelvingMinColumns = (module: Pick<WardrobeModule, "shelvingKind">) =>
  module.shelvingKind === "chest" ? 1 : SHELVING_MIN_COLUMNS;

export const CHEST_MAX_COLUMNS = 4;
/**
 * Сколько колонок ящиков помещается в комод: ящик не уже
 * SHELVING_DRAWERS_MIN_WIDTH_MM, но пролёт не шире допустимого.
 */
export const chestMaxColumns = (width: number) => {
  const byDrawer = Math.floor((width - SHELVING_PANEL_THICKNESS_MM) / (SHELVING_DRAWERS_MIN_WIDTH_MM + SHELVING_PANEL_THICKNESS_MM));
  return Math.max(minShelvingColumnsForWidth(width, 1), Math.min(CHEST_MAX_COLUMNS, byDrawer));
};

export const minShelvingColumnsForWidth = (width: number, minimum = SHELVING_MIN_COLUMNS) => {
  const inner = Math.max(0, width - SHELVING_PANEL_THICKNESS_MM * 2);
  let columns = 1;
  while (columns < SHELVING_MAX_COLUMNS) {
    const span = (inner - SHELVING_PANEL_THICKNESS_MM * (columns - 1)) / columns;
    if (span <= SHELVING_MAX_CLEAR_SPAN_MM) break;
    columns += 1;
  }
  return clamp(columns, minimum, SHELVING_MAX_COLUMNS);
};

export const maxShelvingColumnsForWidth = (width: number, minimum = SHELVING_MIN_COLUMNS) =>
  clamp(
    Math.floor(
      (Math.max(SHELVING_MIN_WIDTH_MM, width) -
        SHELVING_PANEL_THICKNESS_MM) /
        (SHELVING_MIN_CELL_WIDTH_MM + SHELVING_PANEL_THICKNESS_MM)
    ),
    minimum,
    SHELVING_MAX_COLUMNS
  );

export const maxShelvingRowsForHeight = (
  height: number,
  base: WardrobeModule["shelvingBase"] = "plinth"
) => {
  const baseHeight =
    base === "plinth"
      ? SHELVING_PLINTH_HEIGHT_MM
      : base === "legs"
        ? SHELVING_LEG_HEIGHT_MM
        : 0;
  return clamp(
    Math.floor(
      (Math.max(SHELVING_MIN_HEIGHT_MM, height) -
        baseHeight -
        SHELVING_PANEL_THICKNESS_MM) /
        (SHELVING_MIN_CELL_HEIGHT_MM + SHELVING_PANEL_THICKNESS_MM)
    ),
    SHELVING_MIN_ROWS,
    SHELVING_MAX_ROWS
  );
};

export interface ShelvingGridLayout {
  columns: number;
  rows: number;
  baseHeight: number;
  cellBottom: number;
  cellTop: number;
  cellWidths: number[];
  cellHeights: number[];
  columnStarts: number[];
  columnDividerCenters: number[];
  rowDividerCenters: number[];
  rowStarts: number[];
  /**
   * Ширины ячеек по рядам (замечание Сергея № 19): у ряда со своей раскладкой
   * стойки стоят по-своему. cellWidths/columnStarts выше — общая раскладка,
   * по ней стоят все остальные ряды.
   */
  rowCellWidths: number[][];
  rowColumnStarts: number[][];
  rowColumnDividerCenters: number[][];
  /** У ряда своя раскладка колонок. */
  rowCustomColumns: boolean[];
}

/** Ширина ячейки в свету в этом ряду. */
export const gridCellWidth = (grid: ShelvingGridLayout, row: number, column: number) =>
  grid.rowCellWidths[row]?.[column] ?? grid.cellWidths[column] ?? 0;

/** Левая граница ячейки в свету в этом ряду, мм от центра. */
export const gridColumnStart = (grid: ShelvingGridLayout, row: number, column: number) =>
  grid.rowColumnStarts[row]?.[column] ?? grid.columnStarts[column] ?? 0;

/** Ось стойки справа от колонки dividerIndex в этом ряду. */
export const gridDividerCenter = (grid: ShelvingGridLayout, row: number, dividerIndex: number) =>
  grid.rowColumnDividerCenters[row]?.[dividerIndex] ?? grid.columnDividerCenters[dividerIndex];

export const shelvingCellId = (
  moduleId: string,
  column: number,
  row: number
) => `${moduleId}:cell:${column}:${row}`;

export const parseShelvingCellId = (id: string) => {
  const match = /^(.*):cell:(\d+):(\d+)$/.exec(id);
  if (!match) return null;
  return {
    moduleId: match[1]!,
    column: Number(match[2]),
    row: Number(match[3])
  };
};

export const defaultShelvingCell = (
  moduleId: string,
  column: number,
  row: number
): ShelvingCellConfig => ({
  id: shelvingCellId(moduleId, column, row),
  column,
  row,
  backPanel: false,
  extraShelf: false,
  extraShelfPosition: 0.5,
  extraDivider: false,
  extraDividerPosition: 0.5,
  extraDividerMode: "centered",
  extraDividerAngle: 0,
  leftDividerAngle: 0,
  content: "open",
  diagonalColumnSpan: 2,
  diagonalRowSpan: 2,
  drawerCount: 2,
  doorHinge: "left",
  facade: "wood",
  facadePattern: "plain",
  facadeColor: "white",
  handle: "bar"
});

export const shelvingExtraShelfRange = (cellHeightMm: number) => {
  const halfShelf = SHELVING_PANEL_THICKNESS_MM / 2;
  const largestPossibleClearance = Math.max(
    halfShelf,
    cellHeightMm / 2 - 1
  );
  const clearance = Math.min(
    SHELVING_EXTRA_SHELF_CLEARANCE_MM + halfShelf,
    largestPossibleClearance
  );
  return {
    min: clearance,
    max: Math.max(clearance, cellHeightMm - clearance)
  };
};

export const shelvingExtraShelfHeight = (
  cellHeightMm: number,
  normalizedPosition = 0.5
) => {
  const range = shelvingExtraShelfRange(cellHeightMm);
  return clamp(
    cellHeightMm * clamp(normalizedPosition, 0, 1),
    range.min,
    range.max
  );
};

export const shelvingExtraDividerRange = (cellWidth: number) => ({
  min: SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM + SHELVING_PANEL_THICKNESS_MM / 2,
  max: cellWidth - SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM - SHELVING_PANEL_THICKNESS_MM / 2
});

export const shelvingExtraDividerPosition = (cellWidth: number, position = 0.5) => {
  const range = shelvingExtraDividerRange(cellWidth);
  if (range.max < range.min) return null;
  return clamp(cellWidth * (Number.isFinite(position) ? position : 0.5), range.min, range.max);
};

/** Largest safe yaw for a divider at the requested centre. The calculation
 * preserves a 100 mm opening on both the front and rear edges of the cell.
 */
export const shelvingExtraDividerMaxAngle = (
  cellWidth: number,
  depth: number,
  position = 0.5
) => {
  const center = shelvingExtraDividerPosition(cellWidth, position);
  if (center === null) return 0;
  const boardDepth = Math.max(80, depth - 10);
  const clearance = Math.min(
    center - SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM,
    cellWidth - center - SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM
  );
  if (clearance <= SHELVING_PANEL_THICKNESS_MM / 2) return 0;
  let low = 0;
  let high = (SHELVING_EXTRA_DIVIDER_MAX_ANGLE_DEGREES * Math.PI) / 180;
  for (let index = 0; index < 18; index += 1) {
    const angle = (low + high) / 2;
    const halfProjectedWidth =
      Math.abs(Math.sin(angle)) * boardDepth / 2 +
      Math.abs(Math.cos(angle)) * SHELVING_PANEL_THICKNESS_MM / 2;
    if (halfProjectedWidth <= clearance) low = angle;
    else high = angle;
  }
  return Number(Math.min(
    SHELVING_EXTRA_DIVIDER_MAX_ANGLE_DEGREES,
    low * 180 / Math.PI
  ).toFixed(1));
};

/**
 * Стойку и перегородку ставят под одним стандартным углом (замечание
 * Сергея № 23): поле с градусами убрано, остаётся направление.
 */
export const SHELVING_STANDARD_ANGLE_DEG = 10;

/** Прямо — 0, иначе стандартный угол в ту же сторону. */
export const standardShelvingAngle = (angle: number | undefined) =>
  !Number.isFinite(angle) || Math.abs(Number(angle)) < 0.5
    ? 0
    : Math.sign(Number(angle)) * SHELVING_STANDARD_ANGLE_DEG;

export const shelvingExtraDividerAngle = (
  cellWidth: number,
  depth: number,
  position = 0.5,
  angle = 0
) => {
  const maximum = shelvingExtraDividerMaxAngle(cellWidth, depth, position);
  return clamp(Number.isFinite(angle) ? angle : 0, -maximum, maximum);
};

export interface ShelvingCellBoard {
  kind: "divider" | "shelf";
  /** Centres in mm from the cell's left and bottom inner edges. */
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  /** Rotation around the vertical axis in degrees. */
  rotationY?: number;
  /** Visible width in a straight front elevation after applying yaw. */
  projectedWidth?: number;
  /** Какая это деталь ячейки: «shelf:main», «shelf:0», «divider:main», «divider:1»… */
  ref?: string;
  /** Стеклянная полка: тоньше и прозрачная, в смете — по цене стекла. */
  glass?: boolean;
}

/** Сколько всего полок и перегородок может быть в одной ячейке (каждого вида). */
export const SHELVING_CELL_MAX_BOARDS = 5;

const finitePositions = (values: unknown) =>
  Array.isArray(values)
    ? values.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
        .map(value => clamp(value, 0, 1)).slice(0, SHELVING_CELL_MAX_BOARDS - 1)
    : [];

/**
 * Оси перегородок ячейки слева направо. Проём между соседними не меньше
 * 100 мм в свету; перегородка, которая не помещается, не ставится.
 */
export const shelvingCellDividerXs = (cell: ShelvingCellConfig, width: number) => {
  if (cell.content === "drawers" || cell.content === "diagonal") return [];
  const thickness = SHELVING_PANEL_THICKNESS_MM;
  const range = shelvingExtraDividerRange(width);
  if (range.max < range.min) return [];
  const dividerMode = cell.extraDividerMode ??
    (Math.abs((cell.extraDividerPosition ?? 0.5) - 0.5) > 0.001 ? "offset" : "centered");
  const candidates: Array<{ x: number; ref: string }> = [];
  if (cell.extraDivider) {
    const main = shelvingExtraDividerPosition(width,
      dividerMode === "centered" || dividerMode === "angled" ? 0.5 : cell.extraDividerPosition);
    if (main !== null) candidates.push({ x: main, ref: "divider:main" });
  }
  finitePositions(cell.moreDividers).forEach((position, index) =>
    candidates.push({ x: clamp(width * position, range.min, range.max), ref: `divider:${index}` }));
  const kept: Array<{ x: number; ref: string }> = [];
  for (const candidate of candidates.sort((a, b) => a.x - b.x)) {
    const previous = kept.at(-1);
    if (previous && candidate.x - previous.x < SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM + thickness) continue;
    kept.push(candidate);
  }
  return kept;
};

/** Стеклянные ли полки из moreShelves — массив той же длины, что и полки. */
const moreShelvesGlassFlags = (cell: ShelvingCellConfig) =>
  finitePositions(cell.moreShelves).map((_, index) => Boolean(cell.moreShelvesGlass?.[index]));

/** Высоты полок ячейки снизу вверх; между полками не меньше 100 мм в свету. */
export const shelvingCellShelfYs = (cell: ShelvingCellConfig, height: number) => {
  if (cell.content === "drawers" || cell.content === "diagonal") return [];
  const thickness = SHELVING_PANEL_THICKNESS_MM;
  const candidates: Array<{ y: number; ref: string; glass: boolean }> = [];
  if (cell.extraShelf) candidates.push({ y: shelvingExtraShelfHeight(height, cell.extraShelfPosition), ref: "shelf:main", glass: Boolean(cell.extraShelfGlass) });
  const glassFlags = moreShelvesGlassFlags(cell);
  finitePositions(cell.moreShelves).forEach((position, index) =>
    candidates.push({ y: shelvingExtraShelfHeight(height, position), ref: `shelf:${index}`, glass: glassFlags[index]! }));
  const kept: Array<{ y: number; ref: string; glass: boolean }> = [];
  for (const candidate of candidates.sort((a, b) => a.y - b.y)) {
    const previous = kept.at(-1);
    if (previous && candidate.y - previous.y < SHELVING_EXTRA_SHELF_CLEARANCE_MM + thickness) continue;
    kept.push(candidate);
  }
  return kept;
};

/** Середина самого большого свободного промежутка — туда встаёт новая деталь. */
const largestGapCenter = (occupied: number[], size: number, minimum: number) => {
  const points = [0, ...occupied.sort((a, b) => a - b), size];
  let best: { center: number; gap: number } | null = null;
  for (let index = 1; index < points.length; index += 1) {
    const gap = points[index]! - points[index - 1]!;
    if (!best || gap > best.gap) best = { center: (points[index]! + points[index - 1]!) / 2, gap };
  }
  return best && best.gap >= minimum * 2 + SHELVING_PANEL_THICKNESS_MM * 2 ? best.center : null;
};

/** Добавить полку (обычную или стеклянную) или перегородку в ячейку. null — места нет. */
export const withAddedShelvingBoard = (
  cell: ShelvingCellConfig,
  kind: "shelf" | "divider",
  width: number,
  height: number,
  glass = false
): Partial<ShelvingCellConfig> | null => {
  if (cell.content === "drawers" || cell.content === "diagonal") return null;
  if (kind === "shelf") {
    const ys = shelvingCellShelfYs(cell, height);
    if (ys.length >= SHELVING_CELL_MAX_BOARDS) return null;
    const center = largestGapCenter(ys.map(item => item.y), height, SHELVING_EXTRA_SHELF_CLEARANCE_MM);
    if (center === null) return null;
    const position = Number((center / height).toFixed(4));
    const anyGlass = glass || moreShelvesGlassFlags(cell).some(Boolean);
    return cell.extraShelf
      ? { moreShelves: [...finitePositions(cell.moreShelves), position],
          ...(anyGlass ? { moreShelvesGlass: [...moreShelvesGlassFlags(cell), glass] } : {}) }
      : { extraShelf: true, extraShelfPosition: position, ...(glass || cell.extraShelfGlass ? { extraShelfGlass: glass } : {}) };
  }
  const xs = shelvingCellDividerXs(cell, width);
  if (xs.length >= SHELVING_CELL_MAX_BOARDS) return null;
  if (!cell.extraDivider) {
    if (shelvingExtraDividerPosition(width, 0.5) === null) return null;
    return { extraDivider: true, extraDividerMode: "centered", extraDividerPosition: 0.5, extraDividerAngle: 0 };
  }
  // Новая перегородка — и все перегородки расставляются равными проёмами,
  // как делают ячейки под бутылки. Несколько перегородок — только прямые.
  const count = xs.length + 1;
  const opening = (width - count * SHELVING_PANEL_THICKNESS_MM) / (count + 1);
  if (opening < SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM) return null;
  const positions = Array.from({ length: count }, (_, index) =>
    Number(((opening * (index + 1) + SHELVING_PANEL_THICKNESS_MM * (index + 0.5)) / width).toFixed(4)));
  return {
    extraDivider: true, extraDividerMode: "offset", extraDividerPosition: positions[0], extraDividerAngle: 0,
    moreDividers: positions.slice(1)
  };
};

/** Убрать деталь ячейки по её ref. Если убрана первая, её место занимает следующая. */
export const withoutShelvingBoard = (cell: ShelvingCellConfig, ref: string): Partial<ShelvingCellConfig> => {
  const [kind, slot] = ref.split(":");
  if (kind === "shelf") {
    const more = finitePositions(cell.moreShelves);
    const glass = moreShelvesGlassFlags(cell);
    // Признаки стекла переносим, только если в ячейке есть стеклянные полки.
    const hasGlass = Boolean(cell.extraShelfGlass) || glass.some(Boolean);
    if (slot === "main") {
      if (!more.length) return { extraShelf: false, moreShelves: [], ...(hasGlass ? { extraShelfGlass: false, moreShelvesGlass: [] } : {}) };
      return { extraShelf: true, extraShelfPosition: more[0], moreShelves: more.slice(1),
        ...(hasGlass ? { extraShelfGlass: glass[0], moreShelvesGlass: glass.slice(1) } : {}) };
    }
    return {
      moreShelves: more.filter((_, index) => String(index) !== slot),
      ...(hasGlass ? { moreShelvesGlass: glass.filter((_, index) => String(index) !== slot) } : {})
    };
  }
  const more = finitePositions(cell.moreDividers);
  if (slot === "main") {
    return more.length
      ? { extraDivider: true, extraDividerMode: "offset", extraDividerPosition: more[0], extraDividerAngle: 0, moreDividers: more.slice(1) }
      : { extraDivider: false, moreDividers: [] };
  }
  return { moreDividers: more.filter((_, index) => String(index) !== slot) };
};

/** Передвинуть полку ячейки на новую высоту, мм от низа проёма. */
export const withMovedShelvingShelf = (cell: ShelvingCellConfig, ref: string, height: number, y: number): Partial<ShelvingCellConfig> => {
  const position = Number((clamp(y, 0, height) / Math.max(1, height)).toFixed(4));
  if (ref === "shelf:main") return { extraShelfPosition: position };
  const slot = Number(ref.split(":")[1]);
  const more = finitePositions(cell.moreShelves);
  if (!Number.isInteger(slot) || slot < 0 || slot >= more.length) return {};
  more[slot] = position;
  return { moreShelves: more };
};

/**
 * Передвинуть перегородку ячейки: x — центр, мм от левой стенки проёма
 * (замечание Сергея 15.10: вставленные перегородки должны двигаться мышью).
 * Первая перегородка после сдвига стоит «со смещением», а не по центру.
 */
export const withMovedShelvingDivider = (cell: ShelvingCellConfig, ref: string, width: number, x: number): Partial<ShelvingCellConfig> => {
  const position = Number((clamp(x, 0, width) / Math.max(1, width)).toFixed(4));
  if (ref === "divider:main") return { extraDividerMode: "offset", extraDividerPosition: position, extraDividerAngle: 0 };
  const slot = Number(ref.split(":")[1]);
  const more = finitePositions(cell.moreDividers);
  if (!Number.isInteger(slot) || slot < 0 || slot >= more.length) return {};
  more[slot] = position;
  return { moreDividers: more };
};

export const shelvingDividerEdgeCenters = (board: ShelvingCellBoard) => {
  const shift = Math.sin((board.rotationY ?? 0) * Math.PI / 180) * board.depth / 2;
  return {
    rear: board.x - shift,
    front: board.x + shift
  };
};

/** Largest safe yaw for a main grid divider between two clear openings. */
export const shelvingMainDividerMaxAngle = (
  leftCellWidth: number,
  rightCellWidth: number,
  depth: number
) => {
  const spare = Math.min(leftCellWidth, rightCellWidth) - SHELVING_MIN_CELL_WIDTH_MM;
  if (spare <= 0) return 0;
  const boardDepth = Math.max(80, depth);
  const halfThickness = SHELVING_PANEL_THICKNESS_MM / 2;
  let low = 0;
  let high = (SHELVING_EXTRA_DIVIDER_MAX_ANGLE_DEGREES * Math.PI) / 180;
  for (let index = 0; index < 18; index += 1) {
    const angle = (low + high) / 2;
    const extraProjection =
      Math.abs(Math.sin(angle)) * boardDepth / 2 +
      Math.abs(Math.cos(angle)) * halfThickness -
      halfThickness;
    if (extraProjection <= spare) low = angle;
    else high = angle;
  }
  return Number(Math.min(
    SHELVING_EXTRA_DIVIDER_MAX_ANGLE_DEGREES,
    low * 180 / Math.PI
  ).toFixed(1));
};

export interface ShelvingMainDividerSegment extends ShelvingCellBoard {
  dividerIndex: number;
  row: number;
  /** The cell immediately to the right owns the segment's setting. */
  cellId: string;
}

export const getShelvingMainDividerSegment = (
  module: WardrobeModule,
  dividerIndex: number,
  row: number,
  grid = getShelvingGridLayout(module)
): ShelvingMainDividerSegment | null => {
  const x = gridDividerCenter(grid, row, dividerIndex);
  const height = grid.cellHeights[row];
  const bottom = grid.rowStarts[row];
  const leftWidth = grid.rowCellWidths[row]?.[dividerIndex] ?? grid.cellWidths[dividerIndex];
  const rightWidth = grid.rowCellWidths[row]?.[dividerIndex + 1] ?? grid.cellWidths[dividerIndex + 1];
  if (
    x === undefined || height === undefined || bottom === undefined ||
    leftWidth === undefined || rightWidth === undefined
  ) return null;
  const cellId = shelvingCellId(module.id, dividerIndex + 1, row);
  const config = module.shelvingCells?.find(cell => cell.id === cellId);
  const maximum = shelvingMainDividerMaxAngle(leftWidth, rightWidth, module.depth);
  const rotationY = clamp(
    Number.isFinite(config?.leftDividerAngle) ? Number(config?.leftDividerAngle) : 0,
    -maximum,
    maximum
  );
  const angleRadians = Math.abs(rotationY) * Math.PI / 180;
  return {
    kind: "divider",
    dividerIndex,
    row,
    cellId,
    x,
    y: bottom + height / 2,
    width: SHELVING_PANEL_THICKNESS_MM,
    height,
    depth: module.depth,
    rotationY,
    projectedWidth:
      Math.cos(angleRadians) * SHELVING_PANEL_THICKNESS_MM +
      Math.sin(angleRadians) * module.depth
  };
};

/** Shared construction for 3D, costing and the assembly drawing.
 * The upright is continuous; shelves meet its sides without intersecting it.
 */
export const getShelvingCellBoards = (
  cell: ShelvingCellConfig,
  width: number,
  height: number,
  depth: number
): ShelvingCellBoard[] => {
  if (cell.content === "drawers" || cell.content === "diagonal") return [];
  const thickness = SHELVING_PANEL_THICKNESS_MM;
  const dividers = shelvingCellDividerXs(cell, width);
  const boards: ShelvingCellBoard[] = [];
  const boardDepth = Math.max(80, depth - 10);
  // Под углом может стоять только единственная перегородка.
  const angled = cell.extraDividerMode === "angled" && dividers.length === 1 && dividers[0]!.ref === "divider:main";
  for (const divider of dividers) {
    const rotationY = angled
      ? shelvingExtraDividerAngle(width, depth, divider.x / width, cell.extraDividerAngle ?? 10)
      : 0;
    const angleRadians = Math.abs(rotationY) * Math.PI / 180;
    const projectedWidth =
      Math.cos(angleRadians) * thickness + Math.sin(angleRadians) * boardDepth;
    boards.push({ kind: "divider", x: divider.x, y: height / 2,
      width: thickness, height, depth: boardDepth, rotationY, projectedWidth, ref: divider.ref });
  }
  const shelves = shelvingCellShelfYs(cell, height);
  if (shelves.length) {
    const ranges: Array<[number, number]> = [];
    let cursor = 0;
    for (const divider of dividers) {
      ranges.push([cursor, divider.x - thickness / 2]);
      cursor = divider.x + thickness / 2;
    }
    ranges.push([cursor, width]);
    for (const shelf of shelves) {
      for (const [left, right] of ranges) {
        boards.push({ kind: "shelf", x: (left + right) / 2, y: shelf.y,
          width: Math.max(0, right - left - 4),
          height: shelf.glass ? SHELVING_GLASS_SHELF_THICKNESS_MM : thickness,
          depth: boardDepth, ref: shelf.ref, ...(shelf.glass ? { glass: true } : {}) });
      }
    }
  }
  return boards;
};

/** Resize one clear opening, distributing the difference among the others. */
export const resizeShelvingOpening = (sizes: number[], index: number, requested: number, minimum = 100) => {
  if (!Number.isInteger(index) || index < 0 || index >= sizes.length || !Number.isFinite(requested)) return null;
  const total = sizes.reduce((sum, size) => sum + size, 0);
  if (sizes.length < 2 || total < sizes.length * minimum) return null;
  const value = clamp(requested, minimum, total - minimum * (sizes.length - 1));
  const spare = total - value - minimum * (sizes.length - 1);
  const weights = sizes.map((size, i) => i === index ? 0 : Math.max(0, size - minimum));
  const sum = weights.reduce((a, b) => a + b, 0);
  return sizes.map((_, i) => i === index ? value / total
    : (minimum + spare * (sum > 0 ? weights[i]! / sum : 1 / (sizes.length - 1))) / total);
};

/**
 * Сдвинуть одну стойку: ячейка index получает ширину requested, разницу берёт
 * соседняя справа (у последней — слева); остальные не трогаются (№ 19).
 */
export const moveOneShelvingUpright = (sizes: number[], index: number, requested: number, minimum = SHELVING_MIN_CELL_WIDTH_MM) => {
  if (!Number.isInteger(index) || index < 0 || index >= sizes.length || sizes.length < 2 || !Number.isFinite(requested)) return null;
  const neighbour = index < sizes.length - 1 ? index + 1 : index - 1;
  const pair = sizes[index]! + sizes[neighbour]!;
  if (pair < minimum * 2) return null;
  // Ни одна из двух ячеек не шире допустимого пролёта (если пара это позволяет).
  const low = Math.max(minimum, Math.min(pair - minimum, pair - SHELVING_MAX_CLEAR_SPAN_MM));
  const high = Math.min(pair - minimum, Math.max(minimum, SHELVING_MAX_CLEAR_SPAN_MM));
  const value = low <= high ? clamp(requested, low, high) : clamp(requested, minimum, pair - minimum);
  return sizes.map((size, i) => i === index ? value : i === neighbour ? pair - value : size);
};

export const getShelvingGridLayout = (
  module: WardrobeModule
): ShelvingGridLayout => {
  const thickness = SHELVING_PANEL_THICKNESS_MM;
  const columns = clamp(
    Math.round(module.shelvingColumns ?? 4),
    minShelvingColumnsForWidth(module.width, shelvingMinColumns(module)),
    maxShelvingColumnsForWidth(module.width, shelvingMinColumns(module))
  );
  const rows = clamp(
    Math.round(module.shelvingRows ?? 5),
    SHELVING_MIN_ROWS,
    maxShelvingRowsForHeight(module.height, module.shelvingBase)
  );
  const baseHeight =
    module.shelvingBase === "floor"
      ? 0
      : module.shelvingBase === "legs"
        ? SHELVING_LEG_HEIGHT_MM
        : SHELVING_PLINTH_HEIGHT_MM;
  const availableWidth = Math.max(
    columns,
    module.width - thickness * 2 - thickness * (columns - 1)
  );
  const availableHeight = Math.max(
    rows,
    module.height - baseHeight - thickness * 2 - thickness * (rows - 1)
  );
  const columnRatios = normalizeShelvingRatios(
    module.shelvingColumnRatios,
    columns
  );
  const rowRatios = normalizeShelvingRatios(module.shelvingRowRatios, rows);
  const cellWidths = columnRatios.map((ratio) => ratio * availableWidth);
  const cellHeights = rowRatios.map((ratio) => ratio * availableHeight);
  const placeColumns = (widths: number[]) => {
    const starts: number[] = [];
    const centers: number[] = [];
    let x = -module.width / 2 + thickness;
    widths.forEach((cellWidth, index) => {
      starts.push(x);
      x += cellWidth;
      if (index < widths.length - 1) {
        centers.push(x + thickness / 2);
        x += thickness;
      }
    });
    return { starts, centers };
  };
  const { starts: columnStarts, centers: columnDividerCenters } = placeColumns(cellWidths);
  // Свои ширины ряда: только при той же сетке колонок, иначе — общая раскладка.
  const rowCustomColumns: boolean[] = [];
  const rowCellWidths: number[][] = [];
  const rowColumnStarts: number[][] = [];
  const rowColumnDividerCenters: number[][] = [];
  for (let row = 0; row < rows; row += 1) {
    const own = shelvingKindAllowsRowColumns(module)
      ? module.shelvingRowColumnRatios?.[row]
      : undefined;
    const custom = Array.isArray(own) && own.length === columns;
    const widths = custom ? normalizeShelvingRatios(own, columns).map((ratio) => ratio * availableWidth) : cellWidths;
    const placed = custom ? placeColumns(widths) : { starts: columnStarts, centers: columnDividerCenters };
    rowCustomColumns.push(custom);
    rowCellWidths.push(widths);
    rowColumnStarts.push(placed.starts);
    rowColumnDividerCenters.push(placed.centers);
  }
  const cellBottom = baseHeight + thickness;
  const cellTop = module.height - thickness;
  const rowDividerCenters: number[] = [];
  const rowStarts: number[] = [];
  let y = cellBottom;
  cellHeights.forEach((cellHeight, index) => {
    rowStarts.push(y);
    y += cellHeight;
    if (index < rows - 1) {
      rowDividerCenters.push(y + thickness / 2);
      y += thickness;
    }
  });

  return {
    columns,
    rows,
    baseHeight,
    cellBottom,
    cellTop,
    cellWidths,
    cellHeights,
    columnStarts,
    columnDividerCenters,
    rowDividerCenters,
    rowStarts,
    rowCellWidths,
    rowColumnStarts,
    rowColumnDividerCenters,
    rowCustomColumns
  };
};

/** У двух рядов одинаковая раскладка колонок (с точностью до 0,5 мм). */
export const sameShelvingRowColumns = (grid: ShelvingGridLayout, a: number, b: number) => {
  const first = grid.rowCellWidths[a] ?? grid.cellWidths;
  const second = grid.rowCellWidths[b] ?? grid.cellWidths;
  return first.length === second.length && first.every((width, index) => Math.abs(width - second[index]!) < 0.5);
};

/** Свои ширины ячеек по рядам — у стеллажей; у комодов сетка общая. */
export const shelvingKindAllowsRowColumns = (module: Pick<WardrobeModule, "shelvingKind">) =>
  module.shelvingKind !== "chest";

export interface ShelvingDiagonalSectionLayout {
  cell: ShelvingCellConfig;
  column: number;
  row: number;
  columnSpan: number;
  rowSpan: number;
  left: number;
  right: number;
  bottom: number;
  top: number;
  width: number;
  height: number;
  coveredCellIds: string[];
}

/** Keep independent sections independent, including after saving/reloading.
 * Legacy projects already store merged sections as one anchor with its span.
 */
export const normalizeShelvingDiagonalCells = (
  moduleId: string,
  cells: ShelvingCellConfig[],
  columns: number,
  rows: number
) => {
  const safeColumns = Math.max(1, Math.round(columns));
  const safeRows = Math.max(1, Math.round(rows));
  const covered = new Set<string>();
  const anchors: ShelvingCellConfig[] = [];
  const candidates = cells.filter(cell => cell.content === "diagonal")
    .sort((a, b) => a.row - b.row || a.column - b.column);
  for (const cell of candidates) {
    const column = clamp(Math.round(cell.column), 0, safeColumns - 1);
    const row = clamp(Math.round(cell.row), 0, safeRows - 1);
    const columnSpan = clamp(Math.round(cell.diagonalColumnSpan ?? 2), 1, safeColumns - column);
    const rowSpan = clamp(Math.round(cell.diagonalRowSpan ?? 2), 1, safeRows - row);
    const ids: string[] = [];
    for (let x = column; x < column + columnSpan; x++) {
      for (let y = row; y < row + rowSpan; y++) ids.push(shelvingCellId(moduleId, x, y));
    }
    // Malformed overlapping legacy sections resolve deterministically.
    if (ids.some(id => covered.has(id))) continue;
    ids.forEach(id => covered.add(id));
    anchors.push({
      ...cell, id: shelvingCellId(moduleId, column, row), column, row,
      extraShelf: false, extraDivider: false, leftDividerAngle: 0,
      diagonalColumnSpan: columnSpan, diagonalRowSpan: rowSpan
    });
  }
  return [
    ...cells.filter(cell => cell.content !== "diagonal" && !covered.has(cell.id)),
    ...anchors
  ].sort((a, b) => a.row - b.row || a.column - b.column);
};

export const getShelvingCellSpanRect = (
  module: WardrobeModule,
  column: number,
  row: number,
  requestedColumnSpan = 1,
  requestedRowSpan = 1,
  grid = getShelvingGridLayout(module)
) => {
  const safeColumn = clamp(Math.round(column), 0, grid.columns - 1);
  const safeRow = clamp(Math.round(row), 0, grid.rows - 1);
  const columnSpan = clamp(
    Math.round(requestedColumnSpan),
    1,
    grid.columns - safeColumn
  );
  const rowSpan = clamp(
    Math.round(requestedRowSpan),
    1,
    grid.rows - safeRow
  );
  // Ряды одной объединённой ячейки всегда с одной раскладкой — берём нижний.
  const width =
    (grid.rowCellWidths[safeRow] ?? grid.cellWidths)
      .slice(safeColumn, safeColumn + columnSpan)
      .reduce((sum, value) => sum + value, 0) +
    SHELVING_PANEL_THICKNESS_MM * (columnSpan - 1);
  const height =
    grid.cellHeights
      .slice(safeRow, safeRow + rowSpan)
      .reduce((sum, value) => sum + value, 0) +
    SHELVING_PANEL_THICKNESS_MM * (rowSpan - 1);
  const left = grid.rowColumnStarts[safeRow]?.[safeColumn] ?? grid.columnStarts[safeColumn] ?? -module.width / 2;
  const bottom = grid.rowStarts[safeRow] ?? grid.cellBottom;
  return {
    column: safeColumn,
    row: safeRow,
    columnSpan,
    rowSpan,
    left,
    right: left + width,
    bottom,
    top: bottom + height,
    width,
    height
  };
};

export const getShelvingDiagonalSections = (
  module: WardrobeModule,
  grid = getShelvingGridLayout(module)
): ShelvingDiagonalSectionLayout[] => {
  const occupied = new Set<string>();
  const sections: ShelvingDiagonalSectionLayout[] = [];
  const candidates = normalizeShelvingDiagonalCells(
    module.id,
    module.shelvingCells ?? [],
    grid.columns,
    grid.rows
  )
    .filter((cell) => cell.content === "diagonal")
    .sort((a, b) => a.row - b.row || a.column - b.column);

  candidates.forEach((cell) => {
    const rect = getShelvingCellSpanRect(
      module,
      cell.column,
      cell.row,
      cell.diagonalColumnSpan ?? 2,
      cell.diagonalRowSpan ?? 2,
      grid
    );
    const coveredCellIds: string[] = [];
    for (
      let column = rect.column;
      column < rect.column + rect.columnSpan;
      column += 1
    ) {
      for (let row = rect.row; row < rect.row + rect.rowSpan; row += 1) {
        coveredCellIds.push(shelvingCellId(module.id, column, row));
      }
    }
    if (coveredCellIds.some((id) => occupied.has(id))) return;
    coveredCellIds.forEach((id) => occupied.add(id));
    sections.push({
      cell: {
        ...cell,
        diagonalColumnSpan: rect.columnSpan,
        diagonalRowSpan: rect.rowSpan
      },
      ...rect,
      coveredCellIds
    });
  });
  return sections;
};

export const shelvingDiagonalSectionAtCell = (
  module: WardrobeModule,
  column: number,
  row: number,
  grid = getShelvingGridLayout(module)
) =>
  getShelvingDiagonalSections(module, grid).find(
    (section) =>
      column >= section.column &&
      column < section.column + section.columnSpan &&
      row >= section.row &&
      row < section.row + section.rowSpan
  );

// ---------------------------------------------------------------------------
// Объединённые ячейки

export interface ShelvingMergedCellLayout extends ShelvingDiagonalSectionLayout {}

/** Объединённые ячейки: якорь — нижняя левая, остальные в проект не пишутся. */
export const normalizeShelvingMergedCells = (
  moduleId: string,
  cells: ShelvingCellConfig[],
  columns: number,
  rows: number
) => {
  const covered = new Set<string>();
  // Диагональные секции уже заняли свои ячейки.
  for (const cell of cells.filter(item => item.content === "diagonal")) {
    for (let x = cell.column; x < cell.column + (cell.diagonalColumnSpan ?? 2); x++) {
      for (let y = cell.row; y < cell.row + (cell.diagonalRowSpan ?? 2); y++) covered.add(shelvingCellId(moduleId, x, y));
    }
  }
  const anchors = new Map<string, ShelvingCellConfig>();
  const candidates = cells
    .filter(cell => cell.content !== "diagonal" && ((cell.mergeColumnSpan ?? 1) > 1 || (cell.mergeRowSpan ?? 1) > 1))
    .sort((a, b) => a.row - b.row || a.column - b.column);
  for (const cell of candidates) {
    const columnSpan = clamp(Math.round(cell.mergeColumnSpan ?? 1), 1, columns - cell.column);
    const rowSpan = clamp(Math.round(cell.mergeRowSpan ?? 1), 1, rows - cell.row);
    const ids: string[] = [];
    for (let x = cell.column; x < cell.column + columnSpan; x++) {
      for (let y = cell.row; y < cell.row + rowSpan; y++) ids.push(shelvingCellId(moduleId, x, y));
    }
    if (ids.some(id => covered.has(id)) || columnSpan * rowSpan < 2) {
      anchors.set(cell.id, { ...cell, mergeColumnSpan: undefined, mergeRowSpan: undefined });
      continue;
    }
    ids.forEach(id => covered.add(id));
    anchors.set(cell.id, { ...cell, mergeColumnSpan: columnSpan, mergeRowSpan: rowSpan });
  }
  return cells
    .map(cell => anchors.get(cell.id) ?? cell)
    .filter(cell => cell.content === "diagonal" || anchors.has(cell.id) || !covered.has(cell.id));
};

export const getShelvingMergedCells = (
  module: WardrobeModule,
  grid = getShelvingGridLayout(module)
): ShelvingMergedCellLayout[] =>
  normalizeShelvingMergedCells(module.id, module.shelvingCells ?? [], grid.columns, grid.rows)
    .filter(cell => cell.content !== "diagonal" && ((cell.mergeColumnSpan ?? 1) > 1 || (cell.mergeRowSpan ?? 1) > 1))
    .map(cell => {
      const rect = getShelvingCellSpanRect(module, cell.column, cell.row, cell.mergeColumnSpan, cell.mergeRowSpan, grid);
      const coveredCellIds: string[] = [];
      for (let x = rect.column; x < rect.column + rect.columnSpan; x++) {
        for (let y = rect.row; y < rect.row + rect.rowSpan; y++) coveredCellIds.push(shelvingCellId(module.id, x, y));
      }
      return { cell, ...rect, coveredCellIds };
    });

/** Всё, внутри чего нет полок и стоек сетки: диагональные секции и объединённые ячейки. */
export const getShelvingSpanSections = (module: WardrobeModule, grid = getShelvingGridLayout(module)) =>
  [...getShelvingDiagonalSections(module, grid), ...getShelvingMergedCells(module, grid)];

export interface ShelvingCellLayout {
  id: string;
  column: number;
  row: number;
  columnSpan: number;
  rowSpan: number;
  config: ShelvingCellConfig | undefined;
  /** Проём в свету, мм от центра стеллажа и от пола. */
  left: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Проёмы стеллажа, как их видит человек: обычные ячейки и объединённые.
 * Ячейки внутри диагональных секций и под объединением не возвращаются.
 */
export const getShelvingCellLayouts = (module: WardrobeModule, grid = getShelvingGridLayout(module)): ShelvingCellLayout[] => {
  const cells = new Map((module.shelvingCells ?? []).map(cell => [cell.id, cell]));
  const merged = getShelvingMergedCells(module, grid);
  const covered = new Set([
    ...getShelvingDiagonalSections(module, grid).flatMap(section => section.coveredCellIds),
    ...merged.flatMap(section => section.coveredCellIds.filter(id => id !== section.cell.id))
  ]);
  const byAnchor = new Map(merged.map(section => [section.cell.id, section]));
  const layouts: ShelvingCellLayout[] = [];
  for (let row = 0; row < grid.rows; row++) {
    for (let column = 0; column < grid.columns; column++) {
      const id = shelvingCellId(module.id, column, row);
      if (covered.has(id)) continue;
      const span = byAnchor.get(id);
      layouts.push({
        id, column, row,
        columnSpan: span?.columnSpan ?? 1,
        rowSpan: span?.rowSpan ?? 1,
        config: span?.cell ?? cells.get(id),
        left: span?.left ?? gridColumnStart(grid, row, column),
        bottom: span?.bottom ?? grid.rowStarts[row]!,
        width: span?.width ?? gridCellWidth(grid, row, column),
        height: span?.height ?? grid.cellHeights[row]!
      });
    }
  }
  return layouts;
};

// ---------------------------------------------------------------------------
// Фасады ячеек: накладные и вкладные

/** Шов между соседними накладными фасадами на общей стойке или полке, мм. */
export const SHELVING_FACADE_SEAM_MM = 3;
/** Насколько накладной фасад не доходит до наружной грани корпуса, мм. */
export const SHELVING_FACADE_EDGE_MM = 2;
/** Зазор вкладного фасада в проёме по периметру, мм. */
export const SHELVING_INSET_GAP_MM = 2;
/** Шов между фасадами ящиков одной ячейки, мм. */
export const SHELVING_DRAWER_SEAM_MM = 3;
export const SHELVING_FACADE_THICKNESS_MM = 18;

export const shelvingFacadeMount = (module: Pick<WardrobeModule, "shelvingFacadeMount">): ShelvingFacadeMount =>
  module.shelvingFacadeMount === "inset" ? "inset" : "overlay";

export const SHELVING_FACADE_MOUNT_NAMES: Record<ShelvingFacadeMount, string> = {
  overlay: "Накладные",
  inset: "Вкладные"
};

export interface ShelvingFacadeRect {
  mount: ShelvingFacadeMount;
  /** Фасад целиком (у ящиков — все фасады вместе), мм от центра стеллажа и от пола. */
  left: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Где стоит фасад ячейки. Вкладной — в проёме с зазором 2 мм по периметру.
 * Накладной закрывает торцы: наружные стенки, крышку и дно — почти целиком
 * (2 мм до грани), общие стойки и полки — до середины, минус половина шва
 * между соседними фасадами.
 */
/** Прямоугольник фасада ячейки по её id; null — ячейка не видна (внутри объединения или диагонали). */
export const shelvingCellFrontRect = (module: WardrobeModule, cellId: string, grid = getShelvingGridLayout(module)) => {
  const layout = getShelvingCellLayouts(module, grid).find((candidate) => candidate.id === cellId);
  return layout ? shelvingCellFacadeRect(module, layout, grid) : null;
};

/** Свес нижних фасадов комода вниз, мм: прикрывает цоколь или верх ножек. */
export const SHELVING_FACADE_OVERHANG_OPTIONS_MM = [10, 20, 30, 40] as const;
export const SHELVING_FACADE_OVERHANG_MIN_MM = 10;
export const SHELVING_FACADE_OVERHANG_MAX_MM = 40;

/** Свес из сохранённого значения: 10–40 мм шагом 5; старое «да» — 10 мм. */
export const normalizeShelvingFacadeOverhang = (value: unknown) => {
  const mm = value === true ? SHELVING_FACADE_OVERHANG_MIN_MM : Number(value);
  if (!Number.isFinite(mm) || mm <= 0) return undefined;
  return clamp(Math.round(mm / 5) * 5, SHELVING_FACADE_OVERHANG_MIN_MM, SHELVING_FACADE_OVERHANG_MAX_MM);
};

/**
 * Нижние фасады свисают на 10–40 мм (Сергей, 15.10): только у комода, у
 * накладных фасадов и когда под корпусом цоколь или ножки — на полу свисать некуда.
 */
export const shelvingFacadeOverhangMm = (module: Pick<WardrobeModule, "shelvingKind" | "shelvingFacadeOverhang" | "shelvingFacadeMount" | "shelvingBase" | "stackedOnId">) =>
  module.shelvingKind === "chest" && !module.stackedOnId &&
  shelvingFacadeMount(module) === "overlay" && module.shelvingBase !== "floor"
    ? normalizeShelvingFacadeOverhang(module.shelvingFacadeOverhang) ?? 0 : 0;

export const shelvingCellFacadeRect = (
  module: WardrobeModule,
  layout: Pick<ShelvingCellLayout, "column" | "row" | "columnSpan" | "rowSpan" | "left" | "bottom" | "width" | "height">,
  grid = getShelvingGridLayout(module)
): ShelvingFacadeRect => {
  const mount = shelvingFacadeMount(module);
  if (mount === "inset") {
    const gap = SHELVING_INSET_GAP_MM;
    return {
      mount,
      left: layout.left + gap,
      bottom: layout.bottom + gap,
      width: Math.max(0, layout.width - gap * 2),
      height: Math.max(0, layout.height - gap * 2)
    };
  }
  const thickness = SHELVING_PANEL_THICKNESS_MM;
  const outer = thickness - SHELVING_FACADE_EDGE_MM;
  const shared = thickness / 2 - SHELVING_FACADE_SEAM_MM / 2;
  const leftCover = layout.column === 0 ? outer : shared;
  const rightCover = layout.column + layout.columnSpan >= grid.columns ? outer : shared;
  const bottomCover = layout.row === 0 ? outer + shelvingFacadeOverhangMm(module) : shared;
  // Под столешницей, лежащей на боковинах, фасад заканчивается с зазором — её кромка видна целиком.
  const topCover = layout.row + layout.rowSpan >= grid.rows
    ? shelvingTopOverSides(module) ? -SHELVING_TOP_FACADE_GAP_MM : outer
    : shared;
  return {
    mount,
    left: layout.left - leftCover,
    bottom: layout.bottom - bottomCover,
    width: layout.width + leftCover + rightCover,
    height: layout.height + bottomCover + topCover
  };
};

/** Высота одного фасада ящика: фасады ячейки делят её фасад со швами 3 мм. */
export const shelvingDrawerFacadeHeight = (facadeHeight: number, count: number) =>
  Math.max(0, (facadeHeight - SHELVING_DRAWER_SEAM_MM * (Math.max(1, count) - 1)) / Math.max(1, count));

/**
 * Высоты фасадов ящиков ячейки снизу вверх. Свес комода забирает только нижний
 * ящик: остальные стоят как без свеса (замечание 15.10).
 */
export const shelvingDrawerFrontHeights = (
  module: WardrobeModule,
  layout: Pick<ShelvingCellLayout, "row">,
  facadeHeight: number,
  count: number
) => {
  const overhang = layout.row === 0 ? shelvingFacadeOverhangMm(module) : 0;
  const each = shelvingDrawerFacadeHeight(facadeHeight - overhang, count);
  return Array.from({ length: Math.max(1, count) }, (_, index) => each + (index === 0 ? overhang : 0));
};

/** Проём ячейки с учётом объединения; null — ячейка скрыта внутри другой. */
export const shelvingCellRect = (module: WardrobeModule, cellId: string, grid = getShelvingGridLayout(module)) =>
  getShelvingCellLayouts(module, grid).find(layout => layout.id === cellId) ?? null;

// ---------------------------------------------------------------------------
// Стойки и полки сетки как детали: наведение, удаление участка, поворот

/**
 * Деталь сетки: стойка между колонками («column», index — номер стойки слева
 * направо) или полка между рядами («row», index — номер полки снизу вверх).
 * segment — участок: у стойки ряд, у полки колонка.
 */
export interface ShelvingGridBoard {
  axis: "column" | "row";
  index: number;
  segment: number;
}

export const shelvingGridBoardRef = (board: ShelvingGridBoard) =>
  `grid:${board.axis}:${board.index}:${board.segment}`;

export const parseShelvingGridBoardRef = (ref: string | undefined): ShelvingGridBoard | null => {
  const match = /^grid:(column|row):(\d+):(\d+)$/.exec(ref ?? "");
  return match ? { axis: match[1] as "column" | "row", index: Number(match[2]), segment: Number(match[3]) } : null;
};

export const shelvingGridBoardLabel = (board: ShelvingGridBoard) =>
  board.axis === "column" ? "Стойка сетки" : "Полка сетки";

/** Проём, в который входит ячейка сетки (с учётом объединения), или null. */
const layoutAt = (layouts: ShelvingCellLayout[], column: number, row: number) =>
  layouts.find(layout => column >= layout.column && column < layout.column + layout.columnSpan &&
    row >= layout.row && row < layout.row + layout.rowSpan) ?? null;

/**
 * Убрать участок стойки или полки сетки — значит объединить две ячейки по
 * обе стороны от него. Возвращает область объединения или причину отказа.
 */
export const shelvingGridSegmentMerge = (
  module: WardrobeModule,
  board: ShelvingGridBoard,
  grid = getShelvingGridLayout(module)
): { area: { left: number; right: number; bottom: number; top: number } | null; problem: string | null } => {
  const layouts = getShelvingCellLayouts(module, grid);
  const column = board.axis === "column" ? board.index : board.segment;
  const row = board.axis === "column" ? board.segment : board.index;
  const first = layoutAt(layouts, column, row);
  const second = board.axis === "column" ? layoutAt(layouts, column + 1, row) : layoutAt(layouts, column, row + 1);
  if (!first || !second) return { area: null, problem: "Рядом диагональная секция: этот участок убрать нельзя." };
  if (first.id === second.id) return { area: null, problem: "Этого участка уже нет: ячейки объединены." };
  const aligned = board.axis === "column"
    ? first.row === second.row && first.rowSpan === second.rowSpan
    : first.column === second.column && first.columnSpan === second.columnSpan;
  if (!aligned) {
    return { area: null, problem: board.axis === "column"
      ? "Ячейки слева и справа разной высоты: сначала разъедините объединённую."
      : "Ячейки снизу и сверху разной ширины: сначала разъедините объединённую." };
  }
  const area = {
    left: Math.min(first.column, second.column),
    right: Math.max(first.column + first.columnSpan, second.column + second.columnSpan) - 1,
    bottom: Math.min(first.row, second.row),
    top: Math.max(first.row + first.rowSpan, second.row + second.rowSpan) - 1
  };
  return { area, problem: shelvingMergeProblem(module, area, grid) };
};

export type ShelvingMergeSide = "left" | "right" | "up" | "down";

/**
 * Объединить ячейку (в том числе уже объединённую) с соседней с одной
 * стороны. Соседняя может быть тоже объединённой — берётся целиком, если
 * совпадает по высоте (слева/справа) или по ширине (сверху/снизу).
 */
export const shelvingMergeTowards = (
  module: WardrobeModule,
  cellId: string,
  side: ShelvingMergeSide,
  grid = getShelvingGridLayout(module)
): { area: { left: number; right: number; bottom: number; top: number } | null; problem: string | null } => {
  const own = getShelvingCellLayouts(module, grid).find(layout => layout.id === cellId);
  if (!own) return { area: null, problem: "Ячейка не найдена." };
  const board: ShelvingGridBoard =
    side === "right" ? { axis: "column", index: own.column + own.columnSpan - 1, segment: own.row }
      : side === "left" ? { axis: "column", index: own.column - 1, segment: own.row }
        : side === "up" ? { axis: "row", index: own.row + own.rowSpan - 1, segment: own.column }
          : { axis: "row", index: own.row - 1, segment: own.column };
  const limit = board.axis === "column" ? grid.columns - 1 : grid.rows - 1;
  if (board.index < 0 || board.index >= limit) {
    return { area: null, problem: { left: "Слева нет ячейки.", right: "Справа нет ячейки.", up: "Сверху нет ячейки.", down: "Снизу нет ячейки." }[side] };
  }
  return shelvingGridSegmentMerge(module, board, grid);
};

/**
 * Участки стойки по рядам. visible — ряды, где участок есть (не спрятан
 * внутри объединения). rotatable — где его можно повернуть: по обе стороны
 * открытые ячейки, а сам участок хранится в ячейке справа, которая не
 * спрятана под объединением, и соседние проёмы достаточно широки.
 */
export const shelvingUprightRows = (
  module: WardrobeModule,
  dividerIndex: number,
  grid = getShelvingGridLayout(module)
) => {
  const visible: number[] = [];
  const rotatable: number[] = [];
  if (dividerIndex < 0 || dividerIndex >= grid.columns - 1) return { visible, rotatable, maximum: 0 };
  // Предел поворота — по самому узкому ряду: у рядов могут быть свои ширины (№ 19).
  const rowMaximum = (row: number) => shelvingMainDividerMaxAngle(gridCellWidth(grid, row, dividerIndex),
    gridCellWidth(grid, row, dividerIndex + 1), module.depth);
  const layouts = getShelvingCellLayouts(module, grid);
  let maximum = Infinity;
  for (let row = 0; row < grid.rows; row++) {
    const left = layoutAt(layouts, dividerIndex, row);
    const right = layoutAt(layouts, dividerIndex + 1, row);
    if (!left || !right || left.id === right.id) continue;
    visible.push(row);
    if (rowMaximum(row) < 3 || right.id !== shelvingCellId(module.id, dividerIndex + 1, row)) continue;
    if ((left.config?.content ?? "open") !== "open" || (right.config?.content ?? "open") !== "open") continue;
    rotatable.push(row);
    maximum = Math.min(maximum, rowMaximum(row));
  }
  if (!Number.isFinite(maximum)) {
    maximum = Math.min(...Array.from({ length: grid.rows }, (_, row) => rowMaximum(row)));
  }
  return { visible, rotatable, maximum: Number.isFinite(maximum) ? maximum : 0 };
};

export const shelvingUprightRotatableRows = (
  module: WardrobeModule,
  dividerIndex: number,
  grid = getShelvingGridLayout(module)
) => shelvingUprightRows(module, dividerIndex, grid).rotatable;

/** Можно ли объединить прямоугольник ячеек. Возвращает причину отказа или null. */
export const shelvingMergeProblem = (
  module: WardrobeModule,
  area: { left: number; right: number; bottom: number; top: number },
  grid = getShelvingGridLayout(module)
) => {
  if (area.left < 0 || area.bottom < 0 || area.right >= grid.columns || area.top >= grid.rows) return "Область выходит за стеллаж.";
  if (area.left === area.right && area.bottom === area.top) return "Выберите хотя бы две соседние ячейки.";
  const inside = (column: number, row: number) =>
    column >= area.left && column <= area.right && row >= area.bottom && row <= area.top;
  for (const section of getShelvingSpanSections(module, grid)) {
    const corners = [[section.column, section.row], [section.column + section.columnSpan - 1, section.row + section.rowSpan - 1]];
    const overlaps = section.column <= area.right && section.column + section.columnSpan - 1 >= area.left &&
      section.row <= area.top && section.row + section.rowSpan - 1 >= area.bottom;
    if (!overlaps) continue;
    if (section.cell.content === "diagonal") return "В области есть диагональная секция.";
    if (!corners.every(([column, row]) => inside(column!, row!))) return "Уже объединённая ячейка попадает в область частично.";
  }
  // Объединить по вертикали можно только ряды с одной раскладкой колонок (№ 19).
  if (area.top > area.bottom) {
    for (let row = area.bottom + 1; row <= area.top; row++) {
      if (!sameShelvingRowColumns(grid, area.bottom, row)) return "У этих рядов разная ширина ячеек — по вертикали их не объединить.";
    }
  }
  const columnSpan = area.right - area.left + 1;
  const rect = getShelvingCellSpanRect(module, area.left, area.bottom, columnSpan, area.top - area.bottom + 1, grid);
  // Пролёт проверяется, только если объединение убирает стойку. Объединить по
  // вертикали уже широкий проём можно: новых длинных полок не появляется,
  // уходит только полка между ячейками (приёмка 04.10, 8.20).
  const sections = getShelvingSpanSections(module, grid);
  const removesStand = columnSpan > 1 && Array.from({ length: area.top - area.bottom + 1 }, (_, index) => area.bottom + index)
    .some(row => !sections.some(section => section.column === area.left && section.columnSpan === columnSpan &&
      row >= section.row && row <= section.row + section.rowSpan - 1));
  // Допуск 0,5 мм: ячейка ровно 1200 мм в свету из-за округления долей бывала
  // «1200,0001» и не объединялась без понятной причины (приёмка 05.10, sc-07).
  if (removesStand && rect.width > SHELVING_MAX_CLEAR_SPAN_MM + 0.5) return `Пролёт ${Math.round(rect.width)} мм шире допустимых ${SHELVING_MAX_CLEAR_SPAN_MM} мм: полка прогнётся.`;
  return null;
};
